import { ChangeDetectionStrategy, Component, input, output, signal } from '@angular/core';
import { RouterModule } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { DesignIssuesComponent } from '../../design-issues/design-issues.component';
import {
  lucideCircleCheck,
  lucideCopy,
  lucideCopyPlus,
  lucideEllipsis,
  lucidePencil,
  lucideTrash2,
} from '@ng-icons/lucide';
import { HlmBadgeImports } from '@spartan-ng/helm/badge';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmDropdownMenuImports } from '@spartan-ng/helm/dropdown-menu';
import { HlmSeparatorImports } from '@spartan-ng/helm/separator';
import { HlmSheetImports } from '@spartan-ng/helm/sheet';
import { HlmTypographyImports } from '@spartan-ng/helm/typography';
import {
  canActivateDesign,
  otherVoucherType,
  ptToMm,
  VoucherDesign,
  VoucherType,
  voucherTypeLabel,
} from '../../../../../../services/voucher-design.service';
import { DesignThumbnailComponent } from '../../design-thumbnail/design-thumbnail.component';

const SOURCE_LABELS: Record<string, string> = { pdf: 'PDF', svg: 'SVG', image: 'Bild' };

/** One design in the library: thumbnail, badges and its actions (menu, sheet on phones). */
@Component({
  selector: 'app-design-card',
  standalone: true,
  imports: [
    DesignIssuesComponent,
    RouterModule,
    NgIcon,
    HlmBadgeImports,
    HlmButtonImports,
    HlmDropdownMenuImports,
    HlmSeparatorImports,
    HlmSheetImports,
    HlmTypographyImports,
    DesignThumbnailComponent,
  ],
  providers: [
    provideIcons({
      lucideCircleCheck,
      lucideCopy,
      lucideCopyPlus,
      lucideEllipsis,
      lucidePencil,
      lucideTrash2,
    }),
  ],
  templateUrl: './design-card.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'flex gap-3 rounded-lg border p-3 sm:flex-col sm:gap-2' },
})
export class DesignCardComponent {
  readonly design = input.required<VoucherDesign>();
  readonly isActive = input(false);
  readonly busy = input(false);

  readonly activate = output<VoucherDesign>();
  readonly copy = output<{ design: VoucherDesign; voucherType: VoucherType }>();
  readonly delete = output<VoucherDesign>();

  readonly sheetOpen = signal(false);
  private pendingAction: (() => void) | null = null;

  get typeLabel(): string {
    return voucherTypeLabel(this.design().voucher_type);
  }

  /** Ready and, for designs that show logo or banner, both uploaded. */
  get activatable(): boolean {
    return canActivateDesign(this.design());
  }

  get activateLabel(): string {
    return `Für ${this.typeLabel}e verwenden`;
  }

  get copyLabel(): string {
    return this.design().voucher_type === 'money'
      ? 'Als Produkt-Design kopieren'
      : 'Als Wert-Design kopieren';
  }

  get sizeLabel(): string {
    const d = this.design();
    const size = `${ptToMm(d.page_width_pt)} × ${ptToMm(d.page_height_pt)} mm`;
    if (d.source_kind) return `${size} · ${SOURCE_LABELS[d.source_kind] ?? d.source_kind}`;
    return `${size} · ${d.background_kind === 'template' ? 'Vorlage' : 'Leere Seite'}`;
  }

  onActivate() {
    this.activate.emit(this.design());
  }

  onCopySame() {
    this.copy.emit({ design: this.design(), voucherType: this.design().voucher_type });
  }

  onCopyOther() {
    this.copy.emit({
      design: this.design(),
      voucherType: otherVoucherType(this.design().voucher_type),
    });
  }

  onDelete() {
    this.delete.emit(this.design());
  }

  /** Runs after the sheet has closed, so its focus restore can't steal the next dialog's focus. */
  chooseAction(action: () => void) {
    this.pendingAction = action;
    this.sheetOpen.set(false);
  }

  runPendingAction() {
    const action = this.pendingAction;
    this.pendingAction = null;
    action?.call(this);
  }
}
